<template>
  <div class="upload-page">
    <div class="header">
      <div class="title-section">
        <h1>Area Management</h1>
        <p class="description">Upload your area related data using excel or csv.</p>
      </div>
      <router-link to="/dashboard" class="back-btn">
        <span class="arrow">◀</span> BACK
      </router-link>
    </div>

    <div class="main-card">
      <div class="top-controls">
        <div class="upload-field-container">
          <div class="fieldset">
            <span class="legend">Upload</span>
            <div class="input-wrapper" @click="triggerFileSelect">
              <span class="placeholder" v-if="!selectedFile">Upload your Excel or CSV file</span>
              <span class="file-name" v-else>{{ selectedFile.name }}</span>
            </div>
            <input
              id="file-input"
              ref="fileInput"
              type="file"
              accept=".csv,.xlsx,.xls"
              hidden
              @change="onFileChange"
            />
          </div>
          <div class="file-info">{{ fileStatusText }}</div>
        </div>

        <div class="download-actions">
          <div class="action-group">
            <button class="btn-template" @click="downloadTemplate">DOWNLOAD SAMPLE TEMPLATE</button>
            <button class="btn-download" :disabled="!tableData.length">DOWNLOAD</button>
          </div>
        </div>
      </div>  

      <div class="content-area">
        <div v-if="tableData.length > 0" class="preview-section">
          <div class="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th v-for="(col, index) in headers" :key="index">{{ col }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, rowIndex) in displayedRows" :key="rowIndex">
                  <td v-for="(cell, cellIndex) in row" :key="cellIndex">
                    {{ formatCell(cell, headers[cellIndex]) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div class="table-pagination">
            <span>1–{{ Math.min(10, tableData.length) }} of {{ tableData.length }}</span>
            <div class="pagination-controls">
              Rows per page: 
              <select v-model="rowsPerPage" disabled>
                <option :value="10">10</option>
              </select>
              <button class="nav-btn" disabled>◀</button>
              <span class="page-num">1/5</span>
              <button class="nav-btn" disabled>▶</button>
            </div>
          </div>
        </div>
        
        <div v-else class="empty-state">
          <!-- <div class="dot"></div> -->
        </div>
      </div>

      <div class="footer-actions">
        <button
          class="btn-upload-final"
          :disabled="!selectedFile || loading"
          @click="handleFinalUpload"
        >
          {{ loading ? 'UPLOADING...' : 'UPLOAD' }}
        </button>
      </div>
    </div>

    <div v-if="errorMessage" class="error-toast">
      {{ errorMessage }}
    </div>

    <NotificationSnackbar
      ref="snackbar"
      :message="snackbarMessage"
    />
  </div>
</template>

<script>
import NotificationSnackbar from '@/components/upload_CSV/NotificationSnackbar.vue'
import * as XLSX from 'xlsx'
import { getPermissions } from "@/plugins/permissions";

export default {
  components: { NotificationSnackbar },

  data() {
    return {
      selectedFile: null,
      snackbarMessage: '',
      tableData: [],
      headers: [],
      rowsPerPage: 10,
      loading: false,
      errorMessage: '',
      permissions: {},
    }
  },

  computed: {
    fileStatusText() {
      if (!this.selectedFile) return '0 files (0 B in total)'
      const sizeKB = (this.selectedFile.size / 1024).toFixed(1)
      return `1 file (${sizeKB} KB)`
    },

    displayedRows() {
      return this.tableData.slice(0, 10)
    }
  },

  mounted() {
    const dashboardId = 4;
    this.permissions = getPermissions(dashboardId);
  },

  methods: {
    triggerFileSelect() {
      this.$refs.fileInput.click()
    },

    async onFileChange(e) {
      const file = e.target.files?.[0]
      if (!file) return

      this.selectedFile = file
      this.tableData = []
      this.headers = []
      this.errorMessage = ''
      
      try {
        await this.previewUploadedFile(file)
        this.snackbarMessage = 'File preview loaded'
        this.$nextTick(() => this.$refs.snackbar?.showNotification())
      } catch (err) {
        this.errorMessage = 'Cannot read file'
        this.selectedFile = null
      }
    },

    async handleFinalUpload() {
      if (!this.selectedFile) return
      this.loading = true
      try {
        await new Promise(resolve => setTimeout(resolve, 1500))
        this.snackbarMessage = 'Stock list uploaded successfully!'
        this.$nextTick(() => this.$refs.snackbar?.showNotification())
      } catch (err) {
        this.errorMessage = 'Upload failed'
      } finally {
        this.loading = false
      }
    },

    async previewUploadedFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          try {
            const data = e.target.result
            const workbook = XLSX.read(data, { type: 'array' })
            const sheet = workbook.Sheets[workbook.SheetNames[0]]
            const json = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' })
            if (json.length === 0) throw new Error()
            this.headers = json[0].map(h => (h || '').trim())
            this.tableData = json.slice(1)
            resolve()
          } catch (err) { reject(err) }
        }
        reader.readAsArrayBuffer(file)
      })
    },

    formatCell(value, header) {
      if (typeof value !== 'number') return value || ''
      if (header?.toUpperCase().includes('PRICE')) {
        return 'Rs ' + value.toLocaleString('en-US', { minimumFractionDigits: 2 })
      }
      return value
    },

    downloadTemplate() {
      const headers = ['PROVINCE', 'DISTRICT', 'AREA NAME', 'AREA CODE', 'DESCRIPTION']
      const data = [headers, ['Western Province', 'Colombo', 'Colombo', 'C117-COIN', 'NO']]
      const ws = XLSX.utils.aoa_to_sheet(data)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Template')
      XLSX.writeFile(wb, 'area_management_template.xlsx')
    }
  }
}
</script>

<style lang="scss" scoped>
    @use '../upload_CSV/upload_CSV.scss' as *;
</style>